import React from 'react'
import axios from 'axios'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Grid, IconButton,
  List, ListItem, ListItemText, Paper, FormControlLabel, Switch, CircularProgress
} from '@material-ui/core'
import { Close, Search } from '@material-ui/icons'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
const COST_TYPES = [
  { key: 'labor', label: 'Labor' }, { key: 'equipment', label: 'Equipment' },
  { key: 'material', label: 'Material' }, { key: 'subcontract', label: 'Subcontract' },
  { key: 'disposal', label: 'Disposal' }
]

const getConditionMultiplier = (project) => {
  if (!project?.conditionModifiers) return 1
  return project.conditionModifiers.filter(m => m.enabled).reduce((acc, m) => acc * (m.factor || 1), 1)
}

const SearchDropdown = ({ items, onSelect, renderPrimary, renderSecondary }) => (
  <Paper variant="outlined" style={{ maxHeight: 160, overflow: 'auto', marginBottom: 8 }}>
    <List dense>
      {items.map(item => (
        <ListItem key={item._id} button onClick={() => onSelect(item)}>
          <ListItemText primary={renderPrimary(item)} secondary={renderSecondary(item)} />
        </ListItem>
      ))}
      {items.length === 0 && <ListItem><ListItemText primary="No matches" /></ListItem>}
    </List>
  </Paper>
)

const ComponentBuilder = ({ open, onClose, onSave, lineItem, project, url, editComponent, externalSave }) => {
  const [costType, setCostType] = React.useState('labor')
  const [quickPrice, setQuickPrice] = React.useState(false)
  const [quickUnitCost, setQuickUnitCost] = React.useState('')
  const [componentQty, setComponentQty] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [productionRates, setProductionRates] = React.useState([])
  const [laborRates, setLaborRates] = React.useState([])
  const [equipmentRates, setEquipmentRates] = React.useState([])
  const [materials, setMaterials] = React.useState([])
  const [searchText, setSearchText] = React.useState('')
  const [selectedRate, setSelectedRate] = React.useState(null)
  const [selectedMaterial, setSelectedMaterial] = React.useState(null)
  const [subName, setSubName] = React.useState('')
  const [subUnitCost, setSubUnitCost] = React.useState('')
  const [materialOverride, setMaterialOverride] = React.useState('')
  const [description, setDescription] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    axios.get(`${url}/api/bid-estimation/production-rates`).then(r => setProductionRates(r.data)).catch(() => {})
    axios.get(`${url}/api/bid-estimation/labor-rates`).then(r => setLaborRates(r.data)).catch(() => {})
    axios.get(`${url}/api/bid-estimation/equipment-rates`).then(r => setEquipmentRates(r.data)).catch(() => {})
    axios.get(`${url}/api/bid-estimation/catalog`, { params: { limit: 100, availableToBid: true } }).then(r => setMaterials(r.data)).catch(() => {})
  }, [open, url])

  const resetForm = () => {
    setCostType('labor'); setQuickPrice(false); setQuickUnitCost(''); setComponentQty('')
    setSearchText(''); setSelectedRate(null); setSelectedMaterial(null)
    setSubName(''); setSubUnitCost(''); setMaterialOverride(''); setDescription('')
  }

  React.useEffect(() => {
    if (!open) return
    if (editComponent) {
      setCostType(editComponent.costType || 'labor')
      setComponentQty(editComponent.componentQty || lineItem?.quantity || '')
      setDescription(editComponent.description || '')
      if (editComponent.costType === 'subcontract' || editComponent.costType === 'disposal') {
        setSubName(editComponent.subcontractPricing?.subName || '')
        setSubUnitCost(editComponent.subcontractPricing?.unitCost ?? '')
      }
      if (editComponent.costType === 'material') setMaterialOverride(editComponent.materialPricing?.manualOverride ?? '')
    } else { resetForm(); setComponentQty(lineItem?.quantity ?? '') }
  }, [open, editComponent, lineItem])

  const handleClose = () => { resetForm(); onClose() }

  const filteredProdRates = React.useMemo(() => {
    if (!searchText.trim()) return productionRates.slice(0, 20)
    const q = searchText.toLowerCase()
    return productionRates.filter(r => (r.description || '').toLowerCase().includes(q) || (r.scope || '').toLowerCase().includes(q) || (r.role || '').toLowerCase().includes(q)).slice(0, 20)
  }, [searchText, productionRates])

  const filteredMaterials = React.useMemo(() => {
    if (!searchText.trim()) return materials.slice(0, 20)
    const q = searchText.toLowerCase()
    return materials.filter(m =>
      (m.itemName || '').toLowerCase().includes(q) ||
      (m.itemAlternateName || '').toLowerCase().includes(q) ||
      (m.itemCode || '').toLowerCase().includes(q) ||
      (m.categoryName || '').toLowerCase().includes(q)
    ).slice(0, 20)
  }, [searchText, materials])

  const condMult = getConditionMultiplier(project)

  const computedValues = React.useMemo(() => {
    if (!selectedRate) return null
    const baseRate = selectedRate.baseRate || 0
    const adjustedRate = baseRate * condMult
    const role = selectedRate.role || ''
    let hourlyRate = 0
    if (costType === 'labor') { const lr = laborRates.find(l => l.role === role); hourlyRate = lr?.hourlyRate || lr?.fullyBurdenedRate || 0 }
    else if (costType === 'equipment') { const er = equipmentRates.find(e => e.role === role || e.type === role); hourlyRate = er?.hourlyRate || er?.rate || 0 }
    const qty = parseFloat(componentQty) || 0
    const hours = adjustedRate > 0 ? qty / adjustedRate : 0
    return { baseRate, adjustedRate, role, hourlyRate, hours, total: hours * hourlyRate }
  }, [selectedRate, costType, laborRates, equipmentRates, componentQty, condMult])

  const materialValues = React.useMemo(() => {
    if (!selectedMaterial) return null
    const unitCost = parseFloat(materialOverride) || selectedMaterial.bestPrice || selectedMaterial.aspireCost || 0
    const qty = parseFloat(componentQty) || 0
    return { unitCost, total: qty * unitCost, vendorCount: selectedMaterial.vendorCount || 0 }
  }, [selectedMaterial, componentQty, materialOverride])

  const subTotal = React.useMemo(() => (parseFloat(subUnitCost) || 0) * (parseFloat(componentQty) || 0), [subUnitCost, componentQty])

  const handleSave = () => {
    setSaving(true)
    const payload = { costType, componentQty: parseFloat(componentQty) || 0 }
    if (quickPrice) {
      payload.description = description || `${costType} - quick price`
      payload.quickUnitCost = parseFloat(quickUnitCost) || 0
    } else if (costType === 'labor' || costType === 'equipment') {
      if (!selectedRate && !editComponent) { setSaving(false); return }
      payload.description = description || selectedRate?.description || editComponent?.description || ''
      if (selectedRate) {
        payload.productionRate = { rateId: selectedRate._id, baseRate: selectedRate.baseRate, adjustedRate: (selectedRate.baseRate || 0) * condMult, role: selectedRate.role, hourlyRate: computedValues?.hourlyRate || 0 }
      }
    } else if (costType === 'material') {
      payload.description = description || selectedMaterial?.itemName || editComponent?.description || ''
      if (selectedMaterial) {
        payload.materialPricing = {
          catalogItemId: selectedMaterial.aspireItemId,
          unitCost: materialValues?.unitCost || 0,
          priceSource: selectedMaterial.bestPrice != null
            ? (selectedMaterial.vendorCount > 0 ? 'vendor' : 'manual')
            : 'aspire_cost',
          manualOverride: parseFloat(materialOverride) || undefined
        }
      } else if (materialOverride) payload.materialPricing = { manualOverride: parseFloat(materialOverride) || 0 }
    } else {
      payload.description = description || subName || costType
      payload.subcontractPricing = { subName, unitCost: parseFloat(subUnitCost) || 0 }
    }
    // External save mode: pass payload to parent, don't call API directly
    if (externalSave) {
      onSave(payload)
      setSaving(false)
      handleClose()
      return
    }
    const promise = editComponent
      ? axios.put(`${url}/api/bid-estimation/components/${editComponent._id}`, payload)
      : axios.post(`${url}/api/bid-estimation/line-items/${lineItem._id}/components`, payload)
    promise.then(() => { setSaving(false); handleClose(); onSave() }).catch(err => { console.error(err); setSaving(false) })
  }

  const isLabEquip = costType === 'labor' || costType === 'equipment'
  const isMatl = costType === 'material'
  const isSub = costType === 'subcontract' || costType === 'disposal'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item><Typography variant="h6">{editComponent ? 'Edit Component' : 'Add Component'}</Typography></Grid>
          <Grid item><IconButton size="small" onClick={handleClose}><Close /></IconButton></Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} style={{ marginBottom: 16 }}>
          {COST_TYPES.map(ct => (
            <Grid item key={ct.key}>
              <Button variant={costType === ct.key ? 'contained' : 'outlined'} color={costType === ct.key ? 'primary' : 'default'} size="small"
                onClick={() => { setCostType(ct.key); setSelectedRate(null); setSelectedMaterial(null); setSearchText('') }}>
                {ct.label}
              </Button>
            </Grid>
          ))}
        </Grid>

        <FormControlLabel control={<Switch checked={quickPrice} onChange={e => setQuickPrice(e.target.checked)} size="small" />} label="Quick Price" style={{ marginBottom: 12 }} />

        {quickPrice ? (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}><TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} fullWidth variant="outlined" size="small" /></Grid>
            <Grid item xs={3}><TextField label="$/unit" type="number" value={quickUnitCost} onChange={e => setQuickUnitCost(e.target.value)} fullWidth variant="outlined" size="small" /></Grid>
            <Grid item xs={3}><TextField label="Qty" type="number" value={componentQty} onChange={e => setComponentQty(e.target.value)} fullWidth variant="outlined" size="small" /></Grid>
            <Grid item xs={2}><Typography variant="body2">{fmt((parseFloat(quickUnitCost) || 0) * (parseFloat(componentQty) || 0))}</Typography></Grid>
          </Grid>
        ) : (
          <>
            <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }} />

            {isLabEquip && (
              <>
                <TextField label={`Search ${costType} production rates`} value={searchText} onChange={e => setSearchText(e.target.value)} fullWidth variant="outlined" size="small"
                  InputProps={{ startAdornment: <Search fontSize="small" style={{ marginRight: 8, color: '#999' }} /> }} style={{ marginBottom: 4 }} />
                {searchText && !selectedRate && (
                  <SearchDropdown items={filteredProdRates} onSelect={r => { setSelectedRate(r); setSearchText(r.description); setDescription(r.description) }}
                    renderPrimary={r => r.description} renderSecondary={r => `${r.role} | ${r.baseRate} ${r.unit}/hr`} />
                )}
                {selectedRate && computedValues && (
                  <Paper variant="outlined" style={{ padding: 12, marginBottom: 12, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="body2"><strong>Role:</strong> {computedValues.role}</Typography>
                    <Typography variant="body2"><strong>Base Rate:</strong> {computedValues.baseRate} /hr | <strong>Adjusted:</strong> {computedValues.adjustedRate.toFixed(2)} /hr (x{condMult.toFixed(2)})</Typography>
                    <Typography variant="body2"><strong>Hourly Rate:</strong> {fmt(computedValues.hourlyRate)}</Typography>
                  </Paper>
                )}
                <TextField label="Quantity" type="number" value={componentQty} onChange={e => setComponentQty(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} />
                {computedValues && (
                  <Typography variant="body2" style={{ color: '#1976d2', marginTop: 4 }}>
                    {parseFloat(componentQty) || 0} units / {computedValues.adjustedRate.toFixed(2)} units/hr = {computedValues.hours.toFixed(1)} hrs x {fmt(computedValues.hourlyRate)}/hr = <strong>{fmt(computedValues.total)}</strong>
                  </Typography>
                )}
              </>
            )}

            {isMatl && (
              <>
                <TextField label="Search materials" value={searchText} onChange={e => setSearchText(e.target.value)} fullWidth variant="outlined" size="small"
                  InputProps={{ startAdornment: <Search fontSize="small" style={{ marginRight: 8, color: '#999' }} /> }} style={{ marginBottom: 4 }} />
                {searchText && !selectedMaterial && (
                  <SearchDropdown items={filteredMaterials} onSelect={m => { setSelectedMaterial(m); setSearchText(m.itemName); setDescription(m.itemName) }}
                    renderPrimary={m => m.itemName} renderSecondary={m => `${m.categoryName || ''} | ${m.unitTypeName || ''} | ${fmt(m.bestPrice || m.aspireCost || 0)}`} />
                )}
                {selectedMaterial && materialValues && materialValues.vendorCount > 0 && (
                  <Paper variant="outlined" style={{ padding: 8, marginBottom: 8, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="caption"><strong>Best Price ({materialValues.vendorCount} vendor{materialValues.vendorCount !== 1 ? 's' : ''}):</strong> {fmt(selectedMaterial.bestPrice || 0)}</Typography>
                  </Paper>
                )}
                <TextField label="Manual Override $/unit" type="number" value={materialOverride} onChange={e => setMaterialOverride(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} helperText="Leave blank to use catalog/vendor price" />
                <TextField label="Quantity" type="number" value={componentQty} onChange={e => setComponentQty(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} />
                {materialValues && (
                  <Typography variant="body2" style={{ color: '#388e3c', marginTop: 4 }}>
                    {parseFloat(componentQty) || 0} units x {fmt(materialValues.unitCost)}/unit = <strong>{fmt(materialValues.total)}</strong>
                  </Typography>
                )}
              </>
            )}

            {isSub && (
              <>
                <TextField label={costType === 'subcontract' ? 'Subcontractor Name' : 'Disposal Vendor'} value={subName} onChange={e => setSubName(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} />
                <TextField label="Unit Cost ($/unit)" type="number" value={subUnitCost} onChange={e => setSubUnitCost(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} />
                <TextField label="Quantity" type="number" value={componentQty} onChange={e => setComponentQty(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }} />
                <Typography variant="body2" style={{ color: '#f57c00', marginTop: 4 }}>
                  {parseFloat(componentQty) || 0} units x {fmt(parseFloat(subUnitCost) || 0)}/unit = <strong>{fmt(subTotal)}</strong>
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? <CircularProgress size={24} /> : (editComponent ? 'Update' : 'Add Component')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ComponentBuilder
